/* eslint-disable id-length */
/* eslint-disable react/jsx-no-bind */

import "./style.css";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type Node,
  ReactFlow,
  useReactFlow,
  useNodesState,
  useEdgesState,
  useOnSelectionChange,
  applyNodeChanges,
  applyEdgeChanges,
  ReactFlowProvider,
  useStoreApi,
  FitViewOptions,
  Edge,
  InternalNode,
} from "@xyflow/react";
import { EdgeData, isFailed, NodeType, State, Type } from "app/lib/pipeline";
import { edgeTypes, themeEdge } from "./Edge";
import Background from "./Background";
import usePositionedGraph from "./usePositionedGraph";
import Controls from "./Controls/Controls";
import { ControlButton, ControlPanel } from "./Controls/ControlButton";
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import Icon from "../shared/Icon";
import { getRelatedNodesAndEdges } from "./getNodePath";
import { Emphasis } from "./Node/BaseNode";
import NavigationControls from "./Controls/NavigationControls";
import { nodeTypes } from "./Node";
import { useGraph } from "./useGraph";
import { useHotKey } from "./Controls/useHotKey";
import { useLocation, useNavigate } from "react-router-dom";
import { useBuildPreferencesStore } from "../build/Show/lib/useBuildPreferencesStore";
import { useFilterStore } from "../build/Show/components/Sidebar/useFilterStore";
import { useFilteredStepIds } from "../build/Show/lib/useFilteredStepIds";
import { GoToFailure } from "./Controls";
import { resolveStepUrl } from "../build/Show/lib/urlForView";
import useQueryParams from "../build/Show/lib/useQueryParams";
import Spinner from "app/components/shared/Spinner";
import useCurrentJobOrStep from "app/components/build/Show/lib/useCurrentJobOrStep";
import { useBuildStore } from "app/components/build/Show/lib/useBuildStore";
import { AnimatePresence, motion } from "framer-motion";

const FOCUS_ZOOM = 0.8;
const FOCUS_DURATION = 450;

function setNodeEmphasis(node: Node, emphasis: Emphasis): Node {
  return {
    ...node,
    data: { ...node.data, emphasis },
  };
}

/**
 * The pipeline build canvas.
 *
 * A read-only graph for rendering a pending, running, or finished pipeline build.
 */
export function BuildCanvas() {
  const { build } = useBuild();
  const [initialisedNodes, setInitialisedNodes] = useState(false);
  const [initialisedView, setInitialisedView] = useState(false);

  const location = useLocation();
  const [removeIgnoredSteps, setRemoveIgnoredSteps] = useState(true);
  const totalIgnoredSteps = useMemo(() => {
    return build.steps.filter((step) => step.state === State.Ignored).length || 0;
  }, [build.steps]);

  const [expandedGroupSteps, expandGroupSteps, collapseGroupSteps] = useBuildStore((state) => [
    state.expandedGroupSteps,
    state.expandGroupSteps,
    state.collapseGroupSteps,
  ]);

  // If user doesn't have the collapsed groups feature enabled then default to expanding all groups.
  //
  // Otherwise, collapse all the groups except for failed ones on intial render.
  const defaultExpandedGroupSteps = useMemo(() => {
    if (!Features.BuildCanvasCollapsedGroups) {
      return new Set(
        build.steps.filter((step) => step.type === Type.Group).map((step) => step.uuid),
      );
    }

    return new Set(
      build.steps
        .filter((step) => step.type === Type.Group && isFailed(step))
        .map((step) => step.uuid),
    );
  }, []);

  useEffect(() => {
    expandGroupSteps(Array.from(defaultExpandedGroupSteps));
  }, [defaultExpandedGroupSteps]);

  const graph = useGraph({
    removeIgnoredSteps,
    // Force initial graph render to use defaults, avoiding a potential re-render.
    expandedGroupSteps: !initialisedNodes ? defaultExpandedGroupSteps : expandedGroupSteps,
  });

  const {
    fitView,
    getNode,
    getInternalNode,
    getNodes,
    getEdges,
    getZoom,
    isNodeIntersecting,
    screenToFlowPosition,
  } = useReactFlow();
  const store = useStoreApi();
  const { addSelectedNodes } = store.getState();
  const { currentStep } = useCurrentJobOrStep();
  const filteredStepIds = useFilteredStepIds();
  const filterBy = useFilterStore((state) => state.filterBy);

  /**
   * Get the current node based on the current step ID.
   *
   * If the current step is a child of a collapsed group step, the parent node is returned.
   */
  const getCurrentNode = useCallback(() => {
    if (currentStep?.groupUuid && !expandedGroupSteps.has(currentStep.groupUuid)) {
      return getInternalNode(currentStep.groupUuid);
    }

    if (currentStep?.uuid) {
      return getInternalNode(currentStep.uuid);
    }

    return null;
  }, [currentStep?.uuid, currentStep?.groupUuid, getInternalNode, expandedGroupSteps]);

  /**
   * The active path is the path of nodes and edges that are related to the current node.
   */
  const activePath = useMemo(() => {
    const node = getCurrentNode();
    if (!node) {
      return { edges: [], nodes: new Map() };
    }

    return getRelatedNodesAndEdges([node], getNodes(), getEdges(), getNode);
  }, [getCurrentNode, getNodes, getEdges, getInternalNode, getRelatedNodesAndEdges]);

  /**
   * Theme a node based on selected state, active path, and filter state.
   */
  const themeNode = useCallback(
    (node: Node) => {
      if (node.id === getCurrentNode()?.id) {
        return setNodeEmphasis(node, Emphasis.High);
      }

      if (filterBy && filteredStepIds.size) {
        if (filteredStepIds.has(node.id)) {
          return setNodeEmphasis(node, Emphasis.High);
        }
        return setNodeEmphasis(node, Emphasis.Low);
      }

      if (activePath.nodes.size) {
        if (
          activePath.nodes.has(node.id) ||
          (node.parentId && activePath.nodes.has(node.parentId))
        ) {
          return setNodeEmphasis(node, Emphasis.High);
        }
        return setNodeEmphasis(node, Emphasis.Low);
      }

      return setNodeEmphasis(node, Emphasis.Default);
    },
    [activePath, filteredStepIds, filterBy, expandedGroupSteps, getCurrentNode],
  );

  const focusOnFilteredSteps = useCallback(
    (options?: FitViewOptions) => {
      // Theme the nodes based on the current filter.
      setNodes((nodes) => nodes.map((node) => themeNode(node as Node)));

      // If there are steps to focus on and one isn't already selected, fit the view to the nodes.
      if (filteredStepIds.size && !currentStep?.uuid) {
        fitView({ ...options, nodes: Array.from(filteredStepIds.values()).map((id) => ({ id })) });
      }
    },
    [filteredStepIds, fitView, themeNode],
  );

  // Focus on filtered steps only when the filter changes, not when the graph changes.
  const filterRef = useRef(filterBy);
  useEffect(() => {
    if (filterRef.current !== filterBy) {
      focusOnFilteredSteps({ duration: FOCUS_DURATION, maxZoom: FOCUS_ZOOM });
      filterRef.current = filterBy;
    }
  }, [filterBy, focusOnFilteredSteps]);

  const onInitialised = useCallback(() => {
    setInitialisedNodes(true);
  }, []);

  /**
   * Fit the initial view of the graph.
   *
   * This is used to either focus on a specific step or fit the view to the
   * entire graph. One subtle difference here to other fitView implementations
   * is the ommision of the `duration` property because we want the initial
   * view to be instant.
   */
  const fitInitialView = useCallback(() => {
    const node = getCurrentNode();
    if (node) {
      fitView({ nodes: [{ id: node.id }], maxZoom: FOCUS_ZOOM });
    } else if (filterBy.length) {
      focusOnFilteredSteps({ maxZoom: FOCUS_ZOOM });
    } else {
      fitView({ maxZoom: FOCUS_ZOOM });
    }
  }, [fitView, getCurrentNode, focusOnFilteredSteps]);

  useEffect(() => {
    if (initialisedNodes && !initialisedView) {
      // We need to use requestAnimationFrame here to ensure that the canvas is
      // fully rendered before we fit the view.
      requestAnimationFrame(() => {
        fitInitialView();
        setInitialisedView(true);
      });
    }
  }, [initialisedNodes, fitInitialView]);

  const dockPosition = useBuildPreferencesStore((state) => state.dockPosition);

  /**
   * Select the current step when the stepId or jobId changes.
   */
  useEffect(() => {
    const node = getCurrentNode();
    if (node) {
      addSelectedNodes([node.id]);
    } else {
      addSelectedNodes([]);
    }
  }, [currentStep?.uuid, currentStep?.groupUuid]);

  const canvasRef = useRef<HTMLDivElement>(null);
  const params = useQueryParams();

  /**
   * Check if a node is inside the canvas viewport.
   */
  const isNodeInViewport = useCallback(
    (node: InternalNode) => {
      const zoom = getZoom();
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) {
        return false;
      }

      // Calculate the bounds of the entire canvas based on current zoom level
      const viewportRect = {
        ...screenToFlowPosition({ x: canvasRect.left, y: canvasRect.top }),
        width: canvasRect.width / zoom,
        height: canvasRect?.height / zoom,
      };

      // Make sure we're looking at absolute position rather than relative position
      // since the node might be in a group.
      const nodeRect = {
        x: node.internals.positionAbsolute?.x || 0,
        y: node.internals.positionAbsolute?.y || 0,
        width: node.measured?.width || 0,
        height: node.measured?.height || 0,
      };

      return isNodeIntersecting(nodeRect, viewportRect, false);
    },
    [isNodeIntersecting, screenToFlowPosition, getZoom],
  );

  /**
   * Focus on the selected step when the stepId or jobId changes, or when the dock position changes.
   *
   * We use a ResizeObserver since the drawer and sidebar can change the size of the canvas container.
   */
  useEffect(() => {
    let resizeObserver;
    if (Features.BuildSidebar && canvasRef.current) {
      resizeObserver = new ResizeObserver(() => {
        const node = getCurrentNode();
        if (!node) {
          return;
        }

        // If pushed into state, force the canvas to focus on the node.
        if (location.state?.forceFocus) {
          return fitView({
            nodes: [{ id: node.id }],
            maxZoom: Math.max(FOCUS_ZOOM, getZoom()),
            duration: FOCUS_DURATION,
          });
        }

        // If the node is not in the viewport, fit the view to the node.
        if (!isNodeInViewport(node)) {
          fitView({
            nodes: [{ id: node.id }],
            maxZoom: getZoom(),
            duration: FOCUS_DURATION,
          });
        }

        // Disonnecting the observer within the callback prevents multiple calls to fitView when resizing the drawer or sidebar
        // after the initial render. Meaning when you resize the drawer it doesn't keen moving the canvas around.
        resizeObserver.disconnect();
      });
      resizeObserver.observe(canvasRef.current);
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [
    params.jobUuid,
    params.stepUuid,
    fitView,
    getZoom,
    dockPosition,
    location.state,
    isNodeInViewport,
    getCurrentNode,
  ]);

  const { calculatingLayout, ...positioned } = usePositionedGraph({
    ...graph,
    expandedGroupSteps: !initialisedNodes ? defaultExpandedGroupSteps : expandedGroupSteps,
    onInitialised,
    currentStep,
  });

  const [nodes, setNodes] = useNodesState<Node>(positioned.nodes);
  const [edges, setEdges] = useEdgesState<Edge<EdgeData>>(positioned.edges);

  const onNodesChange = useCallback(
    (changes) => setNodes((nodes) => applyNodeChanges(changes, nodes)),
    [setNodes],
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((edges) => applyEdgeChanges(changes, edges).map(themeEdge)),
    [setEdges],
  );

  // Update the nodes when the graph changes.
  useEffect(() => {
    setNodes(positioned.nodes.map(themeNode));
  }, [positioned.nodes]);

  // Update the edges when the graph changes.
  useEffect(() => {
    setEdges(positioned.edges.map(themeEdge));
  }, [positioned.edges]);

  /**
   * Collapse or expand all group steps in the graph.
   */
  const toggleAllGroupSteps = useCallback(() => {
    const groups = getNodes()
      .filter((node) => node.type === NodeType.Group)
      .map((group) => group.id);

    expandedGroupSteps.size ? collapseGroupSteps(groups) : expandGroupSteps(groups);
  }, [expandedGroupSteps]);

  useHotKey("g", toggleAllGroupSteps);

  /**
   * Reset edge selection and theme the nodes.
   */
  const resetPath = useCallback(() => {
    setEdges((edges) => edges.map((edge) => themeEdge({ ...edge, selected: false })));
    setNodes((nodes) => nodes.map(themeNode));
  }, [setEdges, setNodes, themeNode]);

  /**
   * Highlight all the edges on the active path and theme the nodes.
   */
  const highlightPath = useCallback(() => {
    // Theme the nodes based on the current path.
    setNodes((nodes) => nodes.map(themeNode));

    // Select and style the edges
    const edgeIds = new Set(activePath.edges.map((edge) => edge.id));
    setEdges((edges) =>
      edges.map((edge) => themeEdge({ ...edge, selected: edgeIds.has(edge.id) })),
    );
  }, [setEdges, activePath, setNodes, themeNode]);

  const navigate = useNavigate();

  // Highlight the path of the selected node
  const onChange = useCallback(
    ({ nodes }) => {
      // Reset the URL when clearing selection on the canvas (if it hasn't been reset already).
      //
      // It's possible the URL has already reset when the user navigates by either back/forward
      // or by closing the drawer.
      if (!nodes.length && currentStep?.uuid) {
        navigate(location.pathname);
      }

      if (nodes.length) {
        highlightPath();
      } else {
        resetPath();
      }
    },
    [getCurrentNode, calculatingLayout],
  );

  useOnSelectionChange({
    onChange,
  });

  const [trackpadDetected, setTrackpadDetected] = useState(false);

  return (
    <>
      <ReactFlow
        ref={canvasRef}
        onlyRenderVisibleElements={true}
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        edgesReconnectable={false}
        nodeTypes={nodeTypes}
        nodesConnectable={false}
        panOnScroll={trackpadDetected}
        zoomOnScroll={true}
        panOnScrollSpeed={1.5}
        onMove={(e) => {
          // Once we detect touchpad, we switch on panOnScroll.
          // It introduce no change to mouse only user but ease trackpad user a lot.
          // NOTE: This is also how Figma works.
          if (e && e.type === "wheel" && Math.abs((e as WheelEvent).deltaX) > 0) {
            setTrackpadDetected(true);
          }
        }}
        proOptions={{ hideAttribution: true }}
        elementsSelectable={true}
        nodesDraggable={false}
        onNodesChange={onNodesChange}
        onNodeClick={(event, node) => {
          const stepUrl = resolveStepUrl(node.id);

          if (Features.BuildSidebar) {
            if (event.metaKey || event.ctrlKey) {
              event.preventDefault();
              window.open(stepUrl, "_blank");
              return;
            }
          }

          navigate(stepUrl);
        }}
        onEdgesChange={onEdgesChange}
        edgesFocusable={false}
        nodesFocusable={true}
        minZoom={0.05}
        maxZoom={1}
      >
        <Background />

        {/* Blackout the view until we're ready to fit the graph. A bit of a hack to handle
            the initial layout calculation and remove a flicker. */}
        <AnimatePresence initial={false}>
          {!initialisedView && (
            <motion.div
              className="absolute inset-0 z-10 bg-white"
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.075, ease: "easeInOut" }}
            />
          )}
        </AnimatePresence>

        {calculatingLayout && (
          <Spinner className="absolute right-4 top-4 animate-fade-in-with-delay z-10" />
        )}
      </ReactFlow>

      <Controls fitViewOptions={{ maxZoom: FOCUS_ZOOM }}>
        {totalIgnoredSteps > 0 && (
          <ControlPanel>
            <ControlButton
              label={removeIgnoredSteps ? "Show hidden steps" : "Hide hidden steps"}
              onClick={() => setRemoveIgnoredSteps(!removeIgnoredSteps)}
              toggled={!removeIgnoredSteps}
            >
              <Icon
                icon={removeIgnoredSteps ? "eye" : "eye-strikethrough"}
                className="h-[18px] w-5"
              />
            </ControlButton>
          </ControlPanel>
        )}

        <ControlPanel>
          <GoToFailure />
        </ControlPanel>
      </Controls>

      <NavigationControls />
    </>
  );
}

export default () => (
  <ReactFlowProvider>
    <BuildCanvas />
  </ReactFlowProvider>
);
